import type { ReactNode } from "react";
import React from "react";
import { usePartnerConfig } from "../../partners/hooks";

type Props = Readonly<{
  partnerId: string;
  renderLoading?: ReactNode;
  renderError?: React.ReactElement | null;
  children?: ReactNode;
}>;

/**
 * Component to load the respective partner. Like LCERenderer, renders
 * components per partner load state.
 *
 * Usage:
 *
 * ```typescript
 * <PartnerRenderer partnerId="core" renderLoading={<LoadingPage />} renderError={<ErrorPage />}>
 *   <ComponentDependentOnPartnerCore />
 * <PartnerRenderer/>
 * ```
 */
export const PartnerRenderer = ({ partnerId, renderLoading, renderError, children }: Props) => {
  const [, { isLoading, isError, error }] = usePartnerConfig(partnerId);

  let node = (isLoading ? renderLoading : children) ?? null;

  if (isError && renderError) {
    // Pass in the new error prop shallow merged with the old props.
    node = React.cloneElement(renderError, {
      error,
    });
  }

  return <>{node}</>;
};
